import { useAuthStore } from '~/stores/use-auth/store/authStore';

import {
  BaseWsInstance,
  BaseWsOptions,
  CloseReason,
  DEFAULT_RECONNECT_INTERVAL,
  getReconnectDelay,
  NORMAL_CLOSURE,
  WebSocketStatus,
} from './utils';

const DEFAULT_RECONNECT_ATTEMPTS = 0;

export const useBaseWs = (
  url: string | (() => Promise<string>),
  options: BaseWsOptions = {},
  shouldConnect = true
): BaseWsInstance => {
  const {
    onOpen,
    onClose,
    onMessage,
    onError,
    onReconnectStop,
    shouldReconnect,
    reconnectInterval,
    reconnectAttempts = DEFAULT_RECONNECT_ATTEMPTS,
    retryOnError = false,
  } = options;

  let ws: WebSocket | null = null;
  let reconnectionTimeoutId: number | undefined = undefined;
  let pollTimeoutId: number | undefined = undefined;
  let reconnectAttemptCount = 0;
  let lastMessage: WebSocketEventMap['message'] | null = null;
  let resolvedUrl: string | null = null;
  let isDisconnecting = false;

  const waitForWSConnection = (): Promise<void> => {
    let pollAttempts = 0;

    const executor = (
      resolve: (value: void) => void,
      reject: (reason: string) => void
    ) => {
      if (ws?.readyState === WebSocket.OPEN) {
        resolve();
      } else if (pollAttempts < reconnectAttempts) {
        pollAttempts++;
        pollTimeoutId = window.setTimeout(
          () => executor(resolve, reject),
          DEFAULT_RECONNECT_INTERVAL
        );
      } else {
        reject(
          `Failed to connect to WebSocket. WebSocket readyState is ${ws?.readyState}`
        );
      }
    };

    return new Promise(executor).finally(() => {
      if (pollTimeoutId !== undefined) {
        window.clearTimeout(pollTimeoutId);
        pollTimeoutId = undefined;
      }
    });
  };

  const scheduleReconnect = () => {
    if (reconnectAttemptCount >= reconnectAttempts) {
      onReconnectStop?.(reconnectAttemptCount);
      return;
    }

    const delay = getReconnectDelay(reconnectAttemptCount, reconnectInterval);

    reconnectionTimeoutId = window.setTimeout(() => {
      reconnectAttemptCount++;
      initializeWs();
    }, delay);
  };

  const initializeWs = async () => {
    const token = useAuthStore.getState().token;
    if (!token) {
      throw new Error('No token');
    }

    if (reconnectionTimeoutId !== undefined) {
      window.clearTimeout(reconnectionTimeoutId);
      reconnectionTimeoutId = undefined;
    }

    if (ws) {
      ws.onopen = null;
      ws.onclose = null;
      ws.onmessage = null;
      ws.onerror = null;
      ws.close();
    }

    if (typeof url === 'function') {
      resolvedUrl = await url();
    } else {
      resolvedUrl = url;
    }

    isDisconnecting = false;
    ws = new WebSocket(resolvedUrl, ['bearer', token]);

    ws.onopen = async (event: Event) => {
      try {
        await waitForWSConnection();
        reconnectAttemptCount = 0;
        onOpen?.(event);
      } catch (_error) {
        onError?.(new Event('error'));
      }
    };

    ws.onerror = (event: Event) => {
      onError?.(event);

      if (retryOnError && !isDisconnecting) {
        scheduleReconnect();
      }
    };

    ws.onmessage = (event: MessageEvent) => {
      lastMessage = event;
      onMessage?.(event);
    };

    ws.onclose = (event: CloseEvent) => {
      onClose?.(event);

      if (event.reason === CloseReason.NoConnectionRequired) {
        return;
      }

      if (isDisconnecting) {
        return;
      }

      if (shouldReconnect) {
        if (!shouldReconnect(event)) {
          return;
        }
        scheduleReconnect();
      }
    };
  };

  const connect = () => {
    reconnectAttemptCount = 0;
    isDisconnecting = false;
    initializeWs();
  };

  const disconnect = (code = NORMAL_CLOSURE, reason?: CloseReason) => {
    isDisconnecting = true;

    if (reconnectionTimeoutId !== undefined) {
      window.clearTimeout(reconnectionTimeoutId);
      reconnectionTimeoutId = undefined;
    }

    if (ws) {
      ws.close(code, reason);
    }
  };

  if (shouldConnect) {
    const token = useAuthStore.getState().token;
    if (!token) {
      throw new Error('No token');
    }
    initializeWs();
  }

  return {
    sendMessage: (message: string) => {
      if (ws?.readyState === WebSocket.OPEN) {
        ws.send(message);
      }
    },
    get lastMessage() {
      return lastMessage;
    },
    getWebSocket: () => ws,
    get status(): WebSocketStatus {
      if (reconnectionTimeoutId !== undefined) {
        return WebSocketStatus.CONNECTING;
      }
      if (!ws) {
        return WebSocketStatus.UNINSTANTIATED;
      }
      return ws.readyState as WebSocketStatus;
    },
    get isConnected() {
      return ws?.readyState === WebSocket.OPEN;
    },
    connect,
    disconnect,
    get reconnectAttempt() {
      return reconnectAttemptCount;
    },
  };
};
